import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./ECE.module.css";
import eceData1 from "./ECEData1";
import eceData2 from "./ECEData2";

const allQuizData = [...eceData1, ...eceData2];

const getRandomQuestions = (data, count = 12) => {
  const easy = data.filter((q) => q.level === "easy");
  const medium = data.filter((q) => q.level === "medium");
  const hard = data.filter((q) => q.level === "hard");

  const pickRandom = (arr, n) =>
    arr.sort(() => 0.5 - Math.random()).slice(0, n);

  let selected = [
    ...pickRandom(easy, 4),
    ...pickRandom(medium, 4),
    ...pickRandom(hard, 4),
  ];

  if (selected.length < count) {
    const remaining = data.filter((q) => !selected.includes(q));
    selected = [...selected, ...pickRandom(remaining, count - selected.length)];
  }

  return selected.sort(() => 0.5 - Math.random());
};

const ECE = () => {
  const navigate = useNavigate();
  const [quizData, setQuizData] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10);
  const [error, setError] = useState("");
  const [showRules, setShowRules] = useState(true);
  const [disqualified, setDisqualified] = useState(false);

  const timerRef = useRef(null);

  // Load quiz after rules are accepted
  useEffect(() => {
    if (!showRules) {
      const selected = getRandomQuestions(allQuizData, 12);
      setQuizData(selected);
    }
  }, [showRules]);

  // Timer + Disqualification logic
  useEffect(() => {
    if (quizData.length === 0 || showResults || disqualified) return;

    clearInterval(timerRef.current);
    setTimeLeft(10);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);

          // Disqualify if no answer given
          if (!answers[currentQuestion]) {
            setDisqualified(true);
            return 0;
          }

          // Move to next or submit
          if (currentQuestion < quizData.length - 1) {
            setCurrentQuestion((q) => q + 1);
          } else {
            handleSubmit();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [currentQuestion, quizData, showResults, disqualified]);

  const handleOptionSelect = (option) => {
    if (timeLeft > 0) {
      setAnswers((prev) => ({ ...prev, [currentQuestion]: option }));
      setError("");
    }
  };

  const handleNext = () => {
    if (answers[currentQuestion] === undefined) {
      setError("Please select an answer before proceeding.");
      return;
    }
    setError("");
    setCurrentQuestion((q) => q + 1);
  };

  const handleSubmit = () => {
    clearInterval(timerRef.current);
    let score = 0;
    quizData.forEach((q, i) => {
      if (answers[i] === q.answer) score++;
    });
    setShowResults(true);
    setAnswers((prev) => ({ ...prev, score }));
  };

  const handleRestart = () => {
    clearInterval(timerRef.current);
    setQuizData([]);
    setAnswers({});
    setCurrentQuestion(0);
    setShowResults(false);
    setShowRules(true);
    setDisqualified(false);
    setError("");
    setTimeLeft(10);
  };

  // RULES SCREEN
  if (showRules) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.rulesModal}>
          <h2 className={styles.rulesTitle}>Quiz Rules</h2>
          <ul className={styles.rulesList}>
            <li>
              1️⃣ You have <b>10 seconds</b> per question.
            </li>
            <li>2️⃣ You can change answers before time ends.</li>
            <li>3️⃣ All questions are mandatory.</li>
            <li>
              4️⃣ Fail to answer ➜ <b>Disqualified ❌</b>
            </li>
            <li>5️⃣ Score 0–8 = No Prize.</li>
          </ul>
          <button
            onClick={() => setShowRules(false)}
            className={styles.startButton}
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  // DISQUALIFIED SCREEN
  if (disqualified) {
    return (
      <div className={styles.quizContainer}>
        <motion.div
          className={styles.quizCard}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className={styles.resultsTitle}>❌ You Are Disqualified</h2>
          <p className={styles.congratsText}>You didn’t answer in time.</p>
          <button onClick={() => navigate("/")} className={styles.submitButton}>
            Go to Home 🏠
          </button>
        </motion.div>
      </div>
    );
  }

  // LOADING SCREEN
  if (quizData.length === 0) {
    return (
      <div className={styles.quizContainer}>
        <div className={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  const total = quizData.length;
  const q = quizData[currentQuestion];
  const progress = ((currentQuestion + 1) / total) * 100;

  // MAIN QUIZ SCREEN
  return (
    <div className={styles.quizContainer}>
      <motion.div
        className={styles.quizCard}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {showResults ? (
          <div className={styles.resultsContainer}>
            <div className={styles.congratsIcon}>🎉</div>
            <h2 className={styles.resultsTitle}>Quiz Completed</h2>
            <div className={styles.codeDisplay}>
              <p className={styles.codeLabel}>Your Score:</p>
              <p className={styles.codeValue}>
                {answers.score}/{total}
              </p>
              <p className={styles.scoreMessage}>
                {answers.score <= 8
                  ? "Fair!"
                  : answers.score <= 10
                  ? "Good!"
                  : "Superb!"}
              </p>
            </div>
            <button onClick={() => navigate("/")} className={styles.submitButton}>
              Go Home
            </button>
          </div>
        ) : (
          <>
            <div className={styles.progressBarContainer}>
              <motion.div
                className={styles.progressBar}
                animate={{ width: `${progress}%` }}
              />
            </div>

            <div className={styles.questionHeader}>
              <span>
                Question {currentQuestion + 1}/{total}
              </span>
              <span className={styles.timer}>⏳ {timeLeft}s</span>
            </div>

            <h3 className={styles.questionText}>{q.question}</h3>

            <div className={styles.optionsContainer}>
              {q.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleOptionSelect(opt)}
                  className={`${styles.optionButton} ${
                    answers[currentQuestion] === opt
                      ? styles.selectedOption
                      : ""
                  }`}
                  disabled={timeLeft === 0}
                >
                  {opt}
                </button>
              ))}
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <div className={styles.navigationButtons}>
              {currentQuestion < total - 1 ? (
                <button onClick={handleNext} className={styles.submitButton}>
                  Next
                </button>
              ) : (
                <button onClick={handleSubmit} className={styles.submitButton}>
                  Submit
                </button>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ECE;
